import { useId, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { TermCard } from "../../../api/content/ontologyApi";
import { getTermAlignments, type TermAlignment, type TermMapping } from "../../../api/content/termAlignmentsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { compactIri } from "../../ontology/viewer/types";
import Chip from "../../../ui/feedback/Chip";

/**
 * Each term the change set adds, beside the reference concepts it maps to
 * (`docs/components/checks.md` TA-6): both definitions side by side, both parents, and the
 * evidence for each trigger. The alignment shapes flag what is missing in the checks panel;
 * whether two definitions mean the same is the curator's judgement.
 */
export default function TermAlignmentsPanel({ changeSetId }: { changeSetId: string }) {
  const { t } = useTranslation();
  const headingId = useId();
  const load = useCallback((signal: AbortSignal) => getTermAlignments(changeSetId, signal), [changeSetId]);
  const alignments = useLoad(load);
  if (alignments.state.status === "loading") {
    return null;
  }
  if (alignments.state.status === "failed") {
    return <ErrorNotice error={alignments.state.error} onRetry={alignments.reload} />;
  }
  const value = alignments.state.value;
  // A vocabulary that cannot be read shows even without new terms: every model check is then `cannot_run`.
  if ((value.alignments.length === 0 || value.vocabularies.length === 0) && value.problems.length === 0) {
    return null;
  }
  return (
    <section className="app-card space-y-3 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-base font-semibold">
        {t("reviews.alignment.title")}
      </h3>
      <p className="app-text-muted text-xs">{t("reviews.alignment.note", { vocabularies: value.vocabularies.join(", ") })}</p>
      {value.problems.map((problem) => (
        <p key={problem}>
          <Chip tone="warning">{t("reviews.alignment.unreadable", { problem })}</Chip>
        </p>
      ))}
      {value.alignments.map((alignment) => (
        <TermAlignmentView key={alignment.term.iri} alignment={alignment} />
      ))}
    </section>
  );
}

function TermAlignmentView({ alignment }: { alignment: TermAlignment }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 border-t pt-2">
      <h4 className="app-title text-sm font-semibold" title={alignment.term.iri}>
        {compactIri(alignment.term.iri)}
      </h4>
      {alignment.mappings.length === 0 ? (
        alignment.noReferenceMatch === null ? (
          <p><Chip tone="danger">{t("reviews.alignment.noDecision")}</Chip></p>
        ) : (
          <p className="text-sm">
            <span className="app-text-muted text-xs">{t("reviews.alignment.noReferenceMatch")}</span> {alignment.noReferenceMatch}
          </p>
        )
      ) : (
        alignment.mappings.map((mapping) => <MappedPair key={`${mapping.relation} ${mapping.target}`} term={alignment.term} mapping={mapping} />)
      )}
      {alignment.parentDifference === null ? null : (
        <p className="text-sm">
          <span className="app-text-muted text-xs">{t("reviews.alignment.parentDifference")}</span> {alignment.parentDifference}
        </p>
      )}
      {alignment.triggers.length === 0 ? null : (
        <ul className="space-y-0.5 text-xs">
          {alignment.triggers.map((trigger) => (
            <li key={`${trigger.property} ${trigger.form} ${trigger.language ?? ""}`}>
              <span className="font-mono">{trigger.form}</span>
              {trigger.language === null ? null : <span className="app-text-muted">@{trigger.language}</span>}{" "}
              {trigger.attestedIn.length === 0 ? (
                <Chip tone="danger">{t("reviews.alignment.noEvidence")}</Chip>
              ) : (
                <span className="app-text-muted">{t("reviews.alignment.attestedIn", { references: trigger.attestedIn.join(", ") })}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MappedPair({ term, mapping }: { term: TermCard; mapping: TermMapping }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <p className="app-text-muted text-xs">
        <Chip>skos:{mapping.relation}</Chip>{" "}
        <span title={mapping.target}>{compactIri(mapping.target)}</span>
        {mapping.concept === null ? null : ` · ${mapping.concept.vocabulary}`}
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        <TermColumn heading={t("reviews.alignment.proposed")} term={term} />
        {mapping.concept === null ? (
          <p><Chip tone="warning">{t("reviews.alignment.unknownConcept")}</Chip></p>
        ) : (
          <TermColumn heading={t("reviews.alignment.reference")} term={mapping.concept.term} />
        )}
      </div>
    </div>
  );
}

function TermColumn({ heading, term }: { heading: string; term: TermCard }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 rounded border p-2 text-sm">
      <p className="app-text-muted text-xs">{heading}</p>
      <p className="font-semibold">{term.labels.map((label) => (label.language === null ? label.text : `${label.text} (${label.language})`)).join(" · ") || compactIri(term.iri)}</p>
      <p>{term.definition ?? <span className="app-text-muted">{t("reviews.alignment.noDefinition")}</span>}</p>
      <p className="app-text-muted text-xs">
        {t("reviews.alignment.parents", { parents: term.parents.map(compactIri).join(", ") || "—" })}
      </p>
    </div>
  );
}
